import { ErrorHandler, Injectable } from '@angular/core';
import type { StackFrame } from './stack-parser';

(Error as any).stackTraceLimit = 50;

function toError(value: unknown): Error {
  if (value instanceof Error) {
    return value;
  }
  if (typeof value === 'object' && value !== null && typeof (value as any).message === 'string') {
    const err = new Error((value as any).message);
    err.name = (value as any).name ?? 'Error';
    if (typeof (value as any).url === 'string') (err as any).url = (value as any).url;
    if (typeof (value as any).status === 'number') (err as any).status = (value as any).status;
    return err;
  }
  if (typeof value === 'object' && value !== null) {
    try {
      return new Error(JSON.stringify(value));
    } catch {
      return new Error('[non-serializable object]');
    }
  }
  return new Error(String(value));
}

function isHttpErrorResponse(err: Error): err is Error & { url: string; status: number } {
  return (
    err.name === 'HttpErrorResponse' && typeof (err as any).url === 'string' && typeof (err as any).status === 'number'
  );
}

export async function reportToHeimdall(error: unknown): Promise<void> {
  const isDevServer = (globalThis as any).devServer ?? false;
  if (isDevServer) {
    console.error(error);
    return;
  }

  // A thrown null/undefined (e.g. `throw undefined`, `Promise.reject()`) carries no message or stack;
  // wrapping it in a new Error would synthesize a self-referential frame in this file with value "undefined".
  if (error == null) {
    return;
  }

  let err = toError(error);
  while ((err as any)['ngOriginalError']) {
    err = toError((err as any)['ngOriginalError']);
  }
  const isDynamicImportError = /^Failed to fetch dynamically imported module:/.test(err.message);
  while (!isDynamicImportError && err.cause instanceof Error) {
    err = err.cause;
  }

  // ChunkLoadError: webpack failed to fetch a lazy chunk (network blip, stale SW cache) — not an app bug.
  // "Script error.": browser hides the message/stack for cross-origin scripts without CORS headers — nothing useful to report.
  if (err.name === 'ChunkLoadError' || err.message === 'Script error.') {
    return;
  }

  let frames: StackFrame[] = [];
  try {
    const { parseStack } = await import('./stack-parser');
    frames = err.stack ? parseStack(err.stack) : [];
  } catch {
    // ignore dynamic import errors (e.g. ChunkLoadError)
  }

  if (frames.length > 0 && frames.every((f) => f.filename.includes('pa.apigateway.co/static/recorder.js'))) {
    return;
  }

  if (/^Error retrieving icon .+! Http failure response for .+: 0 /.test(err.message)) {
    return;
  }

  const project = (globalThis as any)['appId'] ?? 'unknown';
  const environment = (globalThis as any)['environment'] ?? 'unknown';
  const deployment = (globalThis as any)['deployment'] ?? 'unknown';

  // status 0 = network-level failure (CORS, request cancelled, no connectivity) — not an app bug.
  if (isHttpErrorResponse(err) && err.status === 0) {
    return;
  }
  if ((err as any).code === 0 && /^ApiError0$/.test(err.name)) {
    return;
  }

  const isAppFrame = (filename: string) =>
    filename.includes('cdn.apigateway.co/') || filename.startsWith('capacitor://localhost/');

  const mappedFrames = frames.map((f) => ({
    filename: f.filename,
    line_number: f.lineNumber,
    col_number: f.colNumber,
    function: f.function,
    in_app: isAppFrame(f.filename),
  }));

  if (isHttpErrorResponse(err)) {
    mappedFrames.unshift({
      filename: err.url,
      line_number: 0,
      col_number: 0,
      function: String(err.status),
      in_app: true,
    });
  }

  let enrichedMessage = err.message;
  const dynamicImportMatch = err.message.match(/^Failed to fetch dynamically imported module:\s*(.+)$/);
  if (dynamicImportMatch) {
    const chunkUrl = dynamicImportMatch[1].trim();
    const isCapacitor = chunkUrl.startsWith('capacitor://');
    const cdnMatch = chunkUrl.match(/cdn[^/]*\.apigateway\.co\/[^.]+\.([^.]+)\.[^/]+\//);
    const urlDeployment = cdnMatch?.[1] ?? 'unknown';

    mappedFrames.unshift({
      filename: chunkUrl,
      line_number: 0,
      col_number: 0,
      function: 'dynamic_import',
      in_app: isAppFrame(chunkUrl),
    });

    let chunkStatus: string | number = 'skipped';
    let contentType = 'skipped';
    let bodySize = 'skipped';
    if (!isCapacitor) {
      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 3000);
        const probe = await fetch(chunkUrl, { method: 'HEAD', signal: controller.signal });
        clearTimeout(timer);
        chunkStatus = probe.status;
        contentType = probe.headers.get('content-type') ?? 'none';
        bodySize = probe.headers.get('content-length') ?? 'unknown';
      } catch {
        chunkStatus = 'network_error';
      }
    }

    const cause = err.cause instanceof Error ? err.cause.message : 'none';
    const mismatch = urlDeployment !== 'unknown' && deployment !== 'unknown' ? urlDeployment !== deployment : 'n/a';
    const sw = !!navigator.serviceWorker?.controller;
    const online = navigator.onLine;
    enrichedMessage = `${err.message} [chunk_status=${chunkStatus} content_type=${contentType} body_size=${bodySize} cause=${cause} online=${online} url_deploy=${urlDeployment} mismatch=${mismatch} capacitor=${isCapacitor} sw=${sw}]`;
  }

  const payload = {
    report: {
      project,
      environment,
      deployment,
      url: window.location.href,
      message: enrichedMessage,
      exception: {
        type: err.name,
        value: enrichedMessage,
        frames: mappedFrames,
      },
    },
  };

  const endpoint =
    (globalThis as any).heimdallEndpoint ??
    'https://heimdall-api-prod.apigateway.co/heimdall.v2.Heimdall/CreateErrorReport';

  // Bare fetch instead of the Heimdall SDK — we only need one endpoint and the SDK would add
  // a significant bundle weight to every app that imports @vendasta/core.
  fetch(endpoint, {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch(() => undefined);
}

@Injectable()
export class HeimdallErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    console.error(error);
    reportToHeimdall(error);
  }
}
